import { Assert } from 'shared/assert';
import { bearing, destination, point } from '@turf/turf';
import { computed, ref, watch, type Ref } from 'vue';
import type { Coordinate } from 'shared/map/domain/coordinate';
import { getRenderedEventCoordinate } from 'shared/map/domain/coordinate';
import { useRafFn } from '@vueuse/core';
import OLMap from 'ol/Map';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Feature } from 'ol';
import { Point, type Geometry } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON.js';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import { getDragPan } from 'shared/map/domain/interactions';
import Fill from 'ol/style/Fill';
import { Color } from 'shared/common/domain/color';
import Stroke from 'ol/style/Stroke';
import { usePreviewRectangleStore } from '../stores/previewRectangleStore';
import { useBlur } from 'shared/common/composables/blur';

export function usePreviewRectangleLayer() {
  const active = ref<boolean>(true);

  const previewShapeStore = usePreviewRectangleStore();
  const moveKeys = usePreviewRectangleMoveKeys();
  const rotateKeys = usePreviewRectangleRotateKeys();
  const geojson = new GeoJSON();

  const moving = ref<boolean>(false);
  const rotating = ref<boolean>(false);
  const moveHover = ref<boolean>(false);
  const rotateHover = ref<boolean>(false);
  const dragPanEnabled = computed(() => !moving.value && !rotating.value);

  let offset: null | Coordinate = null;
  let newCoordinate: null | Coordinate = null;

  useRafFn(() => {
    if (newCoordinate) {
      previewShapeStore.newCoordinate(newCoordinate);
      newCoordinate = null;
    }
  });

  const cursor = computed(() => {
    if (!active.value) return undefined;
    if (moving.value || rotating.value) return 'grabbing';
    if (rotateHover.value) return 'pointer';
    if (moveHover.value) return 'move';
    return undefined;
  });

  function attach(map: OLMap) {
    const { source, rotateIcon, rotateIconLayer, geofenceSource } = createLayer(map);
    const dragPan = getDragPan(map);

    const element = map.getTargetElement();

    watch(cursor, (newCursor) => {
      if (newCursor) {
        element.style.cursor = newCursor;
      } else {
        element.style.removeProperty('cursor');
      }
    });

    watch(dragPanEnabled, (enabled) => {
      if (!dragPan) return;
      dragPan.setActive(enabled);
    });

    element.addEventListener('mousemove', (ev) => {
      if (!active.value) return;
      if (!map.getView()) return;

      try {
        const mouseCoordinate = getRenderedEventCoordinate(map, ev);
        if (!mouseCoordinate) return;
        Assert.definied(mouseCoordinate[0]);
        Assert.definied(mouseCoordinate[1]);
        const features = source.getFeaturesAtCoordinate(mouseCoordinate);
        moveHover.value = features.length > 0;

        const rotateIconFeatures = map.getFeaturesAtPixel(map.getEventPixel(ev), {
          layerFilter: (layer) => layer === rotateIconLayer,
          hitTolerance: 8,
        });

        rotateHover.value = rotateIconFeatures.length > 0;

        if (moving.value) {
          const coordinate = previewShapeStore.coordinate;
          Assert.definied(coordinate);

          if (!offset) {
            offset = {
              longitude: coordinate.longitude - mouseCoordinate[0],
              latitude: coordinate.latitude - mouseCoordinate[1],
            };
          }

          newCoordinate = {
            longitude: mouseCoordinate[0] + offset.longitude,
            latitude: mouseCoordinate[1] + offset.latitude,
          };
        }

        if (rotating.value) {
          const coordinate = previewShapeStore.coordinate;
          Assert.definied(coordinate);
          previewShapeStore.heading =
            bearing(
              point([coordinate.longitude, coordinate.latitude]),
              point([mouseCoordinate[0], mouseCoordinate[1]]),
            ) - 90;
        }
      } catch (error) {}
    });

    element.addEventListener('mouseup', () => {
      moving.value = false;
      rotating.value = false;
    });

    element.addEventListener('mouseleave', () => {
      moving.value = false;
      rotating.value = false;
    });

    element.addEventListener('mousedown', (ev) => {
      if (!active.value) return;
      if (ev.button !== 0) return;
      if (!map.getView()) return;
      const coordinate = getRenderedEventCoordinate(map, ev);
      if (!coordinate) return;
      const features = source.getFeaturesAtCoordinate(coordinate);
      if (features.length > 0) {
        offset = null;
        moving.value = true;
        return;
      }

      const rotateIconFeatures = map.getFeaturesAtPixel(map.getEventPixel(ev), {
        layerFilter: (layer) => layer === rotateIconLayer,
        hitTolerance: 8,
      });

      if (rotateIconFeatures.length > 0) {
        rotating.value = true;
      }
    });

    element.addEventListener('keydown', (ev) => {
      if (!active.value) return;
      moveKeys.onKeyDown(ev);
      rotateKeys.onKeyDown(ev);
      console.log('keydown', 'code:', ev.code);
    });

    element.addEventListener('keyup', (ev) => {
      if (!active.value) return;
      moveKeys.onKeyUp(ev);
      rotateKeys.onKeyUp(ev);
      console.log('keyup', 'code:', ev.code);
    });

    const rotateHandle = computed(() => {
      if (!active.value) return;
      if (rotating.value || moving.value) return undefined;
      const rotationCoordinate = previewShapeStore.rotationCoordinate;
      return rotationCoordinate;
    });

    watch(
      rotateHandle,
      (value) => {
        if (value) {
          const coordinate = value.geometry.coordinates;

          const point = rotateIcon.getGeometry();
          point?.setCoordinates(coordinate);
          rotateIconLayer.setVisible(true);
        } else {
          rotateIconLayer.setVisible(false);
        }
      },
      {},
    );

    watch(
      () => previewShapeStore.allGeoJsonShapes,
      (allShapes) => {
        source.clear();
        geofenceSource.clear();

        for (const previewRectangle of allShapes) {
          const feature = geojson.readFeature(previewRectangle.shape) as Feature<Geometry>;
          feature.setId(previewRectangle.shape.properties?.shapeId);
          source.addFeatures([feature]);

          const geofence = geojson.readFeature(previewRectangle.geofence) as Feature<Geometry>;
          geofenceSource.addFeatures([geofence]);
        }
      },
      { deep: true },
    );

    return source;
  }

  return {
    attach,
    setActive(value: boolean) {
      active.value = value;
    },

  };
}

function createLayer(map: OLMap) {
  const source = new VectorSource();
  const layer = new VectorLayer({
    source,
    style: new Style({
      fill: new Fill({
        color: '#fef3c7',
      }),
      stroke: new Stroke({
        color: '#000000',
      }),
    }),
  });

  const geofenceSource = new VectorSource();
  const geofenceLayer = new VectorLayer({
    source: geofenceSource,
    style: new Style({
      fill: new Fill({
        color: Color.setAlpha('#d8b4fe', 0.6),
      }),
      stroke: new Stroke({
        color: Color.setAlpha('#000000', 0.6),
      }),
    }),
  });

  map.addLayer(geofenceLayer);
  layer.set('name', 'previewRectangleLayer');
  map.addLayer(layer);

  const rotateIcon = new Feature({
    geometry: new Point([0, 0]),
  });

  const rotateHandleStyle = new Style({
    image: new Icon({
      anchor: [31, 30],
      height: 48,
      width: 48,
      anchorXUnits: 'pixels',
      anchorYUnits: 'pixels',
      src: 'ic--baseline-rotate-90-degrees-cw.png',
    }),
  });

  rotateIcon.setStyle(rotateHandleStyle);

  const rotateIconSource = new VectorSource({
    features: [rotateIcon],
  });

  const rotateIconLayer = new VectorLayer({
    source: rotateIconSource,
    visible: false,
  });

  map.addLayer(rotateIconLayer);

  return {
    source,
    layer,
    rotateIconLayer,
    rotateIconSource,
    rotateIcon,
    geofenceLayer,
    geofenceSource,
  };
}

function usePreviewRectangleRotateKeys() {
  const previewShapeStore = usePreviewRectangleStore();

  let rotation: Rotation | null = null;
  const pressedKeyCodes = new Set<string>();

  useRafFn(({ delta }) => {
    if (rotation === null) return;

    rotation.pressDuration += delta;

    if (rotation.pressDuration > 250) {
      const direction = rotationDirection();
      if (direction === 0) return;

      rotation.heading = rotation.heading + (direction * delta) / 20;

      rotation.heading = ((rotation.heading + 540) % 360) - 180;
      previewShapeStore.heading = Math.round(rotation.heading);
    }
  });

  useBlur(() => {
    pressedKeyCodes.clear();
    rotation = null;
  });

  function onKeyDown(ev: KeyboardEvent) {
    pressedKeyCodes.add(ev.code);
    const buttonPressed = isRotateKey(ev.code);

    if (!buttonPressed) return;
    if (rotation) return;

    const direction = rotationDirection();

    if (direction === 0) return;

    previewShapeStore.rotate(direction);

    const heading = previewShapeStore.heading;

    if (heading === undefined) return;

    rotation = {
      heading: heading,
      pressDuration: 0,
    };
  }

  function onKeyUp(ev: KeyboardEvent) {
    console.log('keyup', 'code:', ev.code);
    pressedKeyCodes.delete(ev.code);
    const moveButtonUp = isRotateKey(ev.code);

    if (moveButtonUp) {
      if (anyButtonPressed()) return;
      rotation = null;
    }
  }

  function anyButtonPressed() {
    for (const code of pressedKeyCodes) {
      if (isRotateKey(code)) return true;
    }

    return false;
  }

  function isRotateKey(code: string) {
    return isRotateLeftKey(code) || isRotateRightKey(code);
  }

  function isRotateLeftKey(code: string) {
    return code === 'Numpad7';
  }

  function rotationDirection() {
    return (isRotateLeftPressed() ? -1 : 0) + (isRotateRightPressed() ? 1 : 0);
  }

  function isRotateLeftPressed() {
    for (const code of pressedKeyCodes) {
      if (isRotateLeftKey(code)) return true;
    }

    return false;
  }

  function isRotateRightKey(code: string) {
    return code === 'Numpad9';
  }

  function isRotateRightPressed() {
    for (const code of pressedKeyCodes) {
      if (isRotateRightKey(code)) return true;
    }

    return false;
  }

  return {
    onKeyDown,
    onKeyUp,
  };
}

function usePreviewRectangleMoveKeys() {
  const previewShapeStore = usePreviewRectangleStore();

  let move: Move | null = null;

  const pressedKeyCodes = new Set<string>();

  useRafFn(({ delta }) => {
    if (move === null) return;

    move.pressDuration += delta;

    if (move.pressDuration > 250) {
      const bearing = moveBearing();

      const pointFeature = destination(
        [move.longitude, move.latitude],
        (50 * delta) / 1000,
        bearing,
        { units: 'meters' },
      );
      const coordinates = pointFeature.geometry.coordinates;

      if (coordinates[0] === undefined || coordinates[1] === undefined) return;

      move.longitude = coordinates[0];
      move.latitude = coordinates[1];

      previewShapeStore.newCoordinate(move);
    }
  });

  useBlur(() => {
    pressedKeyCodes.clear();
    move = null;
  });

  function onKeyDown(ev: KeyboardEvent) {
    pressedKeyCodes.add(ev.code);
    const buttonPressed = isMoveKey(ev.code);
    const coordinate = previewShapeStore.coordinate;

    if (!coordinate) return;
    if (!buttonPressed) return;

    if (move) return;

    const bearing = moveBearing();

    const pointFeature = destination([coordinate.longitude, coordinate.latitude], 1, bearing, {
      units: 'meters',
    });
    const coordinates = pointFeature.geometry.coordinates;

    if (coordinates[0] === undefined || coordinates[1] === undefined) return;

    move = {
      pressDuration: 0,
      longitude: coordinates[0],
      latitude: coordinates[1],
    };

    previewShapeStore.newCoordinate(move!);
  }

  function onKeyUp(ev: KeyboardEvent) {
    pressedKeyCodes.delete(ev.code);
    const moveKey = isMoveKey(ev.code);

    if (!moveKey) return;

    if (anyButtonPressed()) return;
    move = null;
  }

  function isMoveKey(code: string) {
    return isLeftKey(code) || isRightKey(code) || isUpKey(code) || isDownKey(code);
  }

  function isLeftKey(code: string) {
    return code === 'Numpad4' || code === 'ArrowLeft';
  }

  function isRightKey(code: string) {
    return code === 'Numpad6' || code === 'ArrowRight';
  }

  function isUpKey(code: string) {
    return code === 'Numpad8' || code === 'ArrowUp';
  }

  function isDownKey(code: string) {
    return code === 'Numpad2' || code === 'ArrowDown';
  }

  function isUpPressed() {
    for (const code of pressedKeyCodes) {
      if (isUpKey(code)) return true;
    }

    return false;
  }

  function isDownPressed() {
    for (const code of pressedKeyCodes) {
      if (isDownKey(code)) return true;
    }

    return false;
  }

  function isLeftPressed() {
    for (const code of pressedKeyCodes) {
      if (isLeftKey(code)) return true;
    }

    return false;
  }

  function isRightPressed() {
    for (const code of pressedKeyCodes) {
      if (isRightKey(code)) return true;
    }

    return false;
  }

  function anyButtonPressed() {
    for (const code of pressedKeyCodes) {
      if (isMoveKey(code)) return true;
    }

    return false;
  }

  function moveBearing() {
    const latDir = (isUpPressed() ? 1 : 0) + (isDownPressed() ? -1 : 0);
    const longDir = (isLeftPressed() ? 1 : 0) + (isRightPressed() ? -1 : 0);

    const bearing = Math.atan2(latDir, longDir) * (180 / Math.PI) - 90;
    return bearing;
  }

  return {
    onKeyDown,
    onKeyUp,
  };
}

export interface EditShape {
  shapeId: string;
  feature: GeoJSON.Feature;
}

interface Rotation {
  pressDuration: number;
  heading: number;
}

interface Move {
  pressDuration: number;
  longitude: number;
  latitude: number;
}
